// Client-side mirror of the spacecraft step in internal/game/spacecombat.go,
// used for prediction of the local craft between server snapshots — the same
// job fdm.js does for a plane, over the orbit_core.js kernel instead of the
// aero model. Not bit-parity (soft reconciliation absorbs drift, the FDM
// rule) but kept line-for-line equivalent: RCS is rate-command with linear
// spool, thrust burns the tank, the kernel integrates, the regolith pins a
// gentle touchdown.
//
// State rides the fdm.js state shape ({pos, vel, att, omega}) plus two
// space fields the prediction owns: `prop` (Δv remaining, m/s — the server
// is authoritative but doesn't wire it yet; the display tracks the local
// integral) and `landed`.

import { stepOrbit, circularVX } from "./orbit_core.js";
import { v3, vNorm, vDot, vCross, qRotate, qConj, qIntegrate } from "./fdm.js";

const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);

function moveToward(v, target, step) {
  if (v < target) {
    v += step;
    if (v > target) v = target;
  } else {
    v -= step;
    if (v < target) v = target;
  }
  return v;
}

// Craft knobs with the server's zero-config defaults (spacecombat.go).
const thrustAccel = (cfg) => (cfg.massKg > 0 && cfg.maxThrustN > 0 ? cfg.maxThrustN / cfg.massKg : 6.0);
export const craftDeltaV = (cfg) => (cfg.deltaVMS > 0 ? cfg.deltaVMS : 360);
const rcsRate = (cfg) => (cfg.rcsRatePS > 0 ? cfg.rcsRatePS : 1.2);
const rcsAccel = (cfg) => (cfg.rcsAccelPS2 > 0 ? cfg.rcsAccelPS2 : 1.8);

// Body rates that swing the nose onto a world direction — mirrors
// spaceSteerRates: axis = nose × target (sine-of-error gain) in the body
// frame, full-rate swing past the antipode, roll always damped.
function steerRates(s, dir, maxRate) {
  const fwd = qRotate(s.att, v3(1, 0, 0));
  let axis = vCross(fwd, dir);
  if (vDot(fwd, dir) < 0) {
    const n = Math.sqrt(vDot(axis, axis));
    axis = n < 0.05 ? qRotate(s.att, v3(0, 0, 1)) : v3(axis.x / n, axis.y / n, axis.z / n);
  }
  const body = qRotate(qConj(s.att), axis);
  return v3(0, clamp(body.y * 3.0, -maxRate, maxRate), clamp(body.z * 3.0, -maxRate, maxRate));
}

// THE ARENA, mirrored (spaceArenaGuard / spaceReturnThrottle in
// spacecombat.go). This has to live in the prediction or the boundary would
// be the one moment in the game where the client and the server disagree
// about who is flying: the server would swing the nose home and the
// prediction would keep obeying a stick nobody is listening to, and the hull
// would rubber-band its way back into the war. Same latch, same release
// fraction, same closing rate, same "only burn once the nose is on the mark".
const ARENA_R = 5000;
const ARENA_RELEASE_FRAC = 0.86;
const ARENA_RETURN_MS = 95;

/**
 * Build the per-tick prediction step for this world's constants.
 * consts: { mu, R, wrapM, ceiling }; surfaceAt(x, z) is the terrain field's.
 * arenaAt() returns the live arena centre {x, y} (it orbits) or null before
 * the first snapshot lands.
 * Returned step(cfg, s, ctl, dt) matches FDM.step's shape (damage arg unused
 * — a spacecraft's modules gate systems, not its physics, and that's M3).
 */
export function makeSpacecraftStep(consts, surfaceAt, arenaAt = () => null) {
  const { mu, R, wrapM } = consts;
  const ceiling = consts.ceiling > 0 ? consts.ceiling : 1050;
  // Wrap-aware offset from a hull to the arena's centre, plus the horizontal
  // distance the rim is measured on (altitude is the ceiling's business).
  const arenaOffset = (c, pos) => {
    let dx = c.x - pos.x;
    if (wrapM > 0) dx = ((((dx % wrapM) + wrapM * 1.5) % wrapM)) - wrapM * 0.5;
    const d = v3(dx, c.y - pos.y, -pos.z);
    return { d, horiz: Math.hypot(d.x, d.z) };
  };
  return function step(cfg, s, ctl, dt) {
    if (s.prop === undefined) s.prop = craftDeltaV(cfg);
    // Freefall: the G-meter reads what the crew feels, and a coasting orbit
    // feels nothing (the aero model's diagnostics, zeroed for vacuum).
    s.alpha = 0;
    s.nz = 0;

    if (s.landed) {
      if (ctl.throttle > 0.6 && s.prop > 0) {
        s.landed = false;
        s.vel = v3(0, 2, 0);
      } else {
        s.omega = v3();
        return;
      }
    }

    // Who has the ship this tick — the pilot, or the autopilot flying it back
    // into the war? Latched exactly as the server latches it.
    const centre = arenaAt();
    let home = null;
    if (centre) {
      const { d, horiz } = arenaOffset(centre, s.pos);
      if (horiz > ARENA_R || s.pos.y > ceiling) s.returning = true;
      else if (s.returning && horiz < ARENA_R * ARENA_RELEASE_FRAC && s.pos.y < ceiling * 0.9) {
        s.returning = false;
      }
      const n = Math.sqrt(vDot(d, d));
      if (s.returning && n > 1) home = v3(d.x / n, d.y / n, d.z / n);
    } else {
      s.returning = false;
    }

    // Attitude: the boundary autopilot, else gyro hold (ctl.sh: 1 prograde,
    // 2 retro), else the stick.
    const maxRate = rcsRate(cfg);
    const accel = rcsAccel(cfg);
    let target = v3();
    const manual =
      Math.abs(ctl.pitch) > 0.15 || Math.abs(ctl.roll) > 0.15 || Math.abs(ctl.yaw) > 0.15;
    if (home) {
      target = steerRates(s, home, maxRate);
    } else if (!manual && (ctl.sh === 1 || ctl.sh === 2)) {
      let dir = s.vel;
      if (ctl.sh === 2) dir = v3(-dir.x, -dir.y, -dir.z);
      if (vDot(dir, dir) > 9) target = steerRates(s, vNorm(dir), maxRate);
    } else {
      target = v3(ctl.roll * maxRate, ctl.yaw * maxRate * 0.7, ctl.pitch * maxRate);
    }
    s.omega.x = moveToward(s.omega.x, target.x, accel * dt);
    s.omega.y = moveToward(s.omega.y, target.y, accel * dt);
    s.omega.z = moveToward(s.omega.z, target.z, accel * dt);
    s.att = qIntegrate(s.att, s.omega, dt);

    // Main engine against the tank. The idle deadband mirrors the server: a
    // throttle lever resting at 1% must not quietly rewrite the orbit.
    let thr = ctl.throttle;
    if (s.returning) {
      // The autopilot's own hand on the lever, and only once the nose is
      // actually on the mark — thrusting through a slew flies the ship
      // sideways out of the war it is trying to re-enter.
      thr = 0;
      if (home && vDot(qRotate(s.att, v3(1, 0, 0)), home) > 0.94) {
        // Measured against the STATION's velocity, because the destination is
        // moving: a hull already matched to the group is not drifting home.
        const vx = circularVX(mu, R, centre.y);
        const rel = v3(s.vel.x - vx, s.vel.y, s.vel.z);
        const closing = vDot(rel, home);
        thr = closing >= ARENA_RETURN_MS ? 0 : clamp((ARENA_RETURN_MS - closing) / ARENA_RETURN_MS, 0, 1);
      }
    }
    if (thr < 0.03 || !(s.prop > 0)) thr = 0;
    let tx = 0, th = 0, tz = 0;
    if (thr > 0) {
      const a = thrustAccel(cfg) * thr;
      const fwd = qRotate(s.att, v3(1, 0, 0));
      tx = fwd.x * a;
      th = fwd.y * a;
      tz = fwd.z * a;
      s.prop = Math.max(0, s.prop - a * dt);
    }

    const os = { x: s.pos.x, h: s.pos.y, z: s.pos.z, vx: s.vel.x, vh: s.vel.y, vz: s.vel.z };
    stepOrbit(os, mu, R, wrapM, dt, tx, th, tz);
    s.pos = v3(os.x, os.h, os.z);
    s.vel = v3(os.vx, os.vh, os.vz);

    // The regolith: mirror the touchdown pin. A hot arrival is the server's
    // call (the death lands by snapshot); prediction just refuses to tunnel.
    const surf = surfaceAt(os.x, os.z);
    if (os.h <= surf + 1.5) {
      s.pos.y = surf + 1.6;
      s.vel = v3();
      const horiz = Math.hypot(os.vx, os.vz);
      if (os.vh > -4 && horiz < 8) {
        s.landed = true;
        s.omega = v3();
      }
    }
  };
}

/**
 * The fire-control mark — mirrors solveSpaceLead in spacecombat.go: the aim
 * point that puts a round from the shooter onto the target, both flying
 * Kepler. Fixed-point on the kernel: propagate the target to the time of
 * flight, fly a trial shell, move the aim by the miss. Positions must share
 * a wrap image (the snapshot fold guarantees it). Writes the world aim point
 * into `out` and returns true, or false when no solution exists inside the
 * round's life — at which range the pip honestly disappears.
 */
export function spaceLeadPoint(shooter, target, muzzle, consts, out) {
  const { mu, R } = consts;
  const prop = (px, py, pz, vx, vy, vz, tf) => {
    const s = { x: px, h: py, z: pz, vx, vh: vy, vz };
    // The live kernel's own dt, landing exactly on tf — a coarser trial
    // with a full-step tail put the pip 26-42 m off the real round on
    // along-track shots (mirrors solveSpaceLead's fix; same lesson the rod
    // solver taught).
    const dtS = 1 / 60;
    for (let t = 0; t < tf; t += dtS) {
      const step = Math.min(dtS, tf - t);
      stepOrbit(s, mu, R, 1e15, step, 0, 0, 0);
    }
    return s;
  };
  let ax = target.pos.x, ay = target.pos.y, az = target.pos.z;
  // To convergence, not to a count (mirrors solveSpaceLead): along-track
  // shots move the flight time with every correction, and four passes left
  // the pip three hit-radii off the real round.
  for (let i = 0; i < 12; i++) {
    const dx = ax - shooter.pos.x, dy = ay - shooter.pos.y, dz = az - shooter.pos.z;
    const dist = Math.hypot(dx, dy, dz);
    if (dist < 1) return false;
    const tf = dist / muzzle;
    if (tf > 6) return false;
    const k = muzzle / dist;
    const tFut = prop(target.pos.x, target.pos.y, target.pos.z, target.vel.x, target.vel.y, target.vel.z, tf);
    const sEnd = prop(
      shooter.pos.x, shooter.pos.y, shooter.pos.z,
      shooter.vel.x + dx * k, shooter.vel.y + dy * k, shooter.vel.z + dz * k, tf,
    );
    const mx = tFut.x - sEnd.x, my = tFut.h - sEnd.h, mz = tFut.z - sEnd.z;
    ax += mx;
    ay += my;
    az += mz;
    if (mx * mx + my * my + mz * mz < 1) break; // within a metre: solved
  }
  out.x = ax;
  out.y = ay;
  out.z = az;
  return true;
}

/**
 * Vis-viva readout for the HUD: apoapsis/periapsis (m above the datum, -1 if
 * the orbit is open) and the local circular speed. Mirrors the bot brain's
 * arithmetic in spaceThink.
 */
export function orbitReadout(s, { mu, R }) {
  const r = R + s.pos.y;
  const vt = s.vel.x * (r / R);
  const v2 = vt * vt + s.vel.y * s.vel.y + s.vel.z * s.vel.z;
  const inv = 2 / r - v2 / mu;
  if (inv <= 0) return { apo: -1, peri: -1, vCirc: Math.sqrt(mu / r) };
  const a = 1 / inv;
  let c2 = a * a - (a * (r * vt) * (r * vt)) / mu;
  if (c2 < 0) c2 = 0;
  const c = Math.sqrt(c2);
  return { apo: a + c - R, peri: a - c - R, vCirc: Math.sqrt(mu / r) };
}
